import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  RemindersSettingsView,
  isPartsReminder,
} from '@/components/organisms/settings-reminders/RemindersSettingsView';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';
import {
  REMINDER_TYPES,
  REVIEW_REQUEST_POLICY_TYPE,
  type ReminderType,
} from '@/types/reminder-policy';

export const Route = createFileRoute('/_dashboard/dashboard/settings/reminders/$type')({
  // NOT-576 — the review-request policy moved to Settings > Reviews. It is still
  // a valid `ReminderType`, so it would otherwise keep rendering an editable
  // second copy here; and had we dropped it from `REMINDER_TYPES` instead, every
  // existing link and bookmark would have hit the "Unknown reminder type" branch
  // below. Send them to the one place the policy now lives.
  beforeLoad: ({ params }) => {
    if (params.type === REVIEW_REQUEST_POLICY_TYPE) {
      throw redirect({
        to: '/dashboard/settings/reviews',
        search: { tab: 'reminder' },
        replace: true,
      });
    }
  },
  component: ReminderPolicyEditorPage,
});

function isValidReminderType(value: string): value is ReminderType {
  return (REMINDER_TYPES as readonly string[]).includes(value);
}

function ReminderPolicyEditorPage() {
  const { type: rawType } = Route.useParams();
  // The parts messages are purchasing (PLAT-FF-1): unknown without it.
  const purchasingEnabled = usePurchasingEnabled();
  const isValid = isValidReminderType(rawType) && (purchasingEnabled || !isPartsReminder(rawType));
  const type = (isValid ? rawType : null) as ReminderType | null;

  if (!isValid || !type) {
    return (
      <div className="max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="size-4" />
          <h2 className="text-sm font-medium">Unknown reminder type</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          <span className="font-mono">{rawType}</span> is not a recognized ReminderType.
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/dashboard/settings/reminders">Back to Reminders</Link>
        </Button>
      </div>
    );
  }

  return <RemindersSettingsView activeType={type} />;
}
